import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { authApi } from "api/auth/api";
import cn from "classnames";
import { Button } from "components/ui/Button";
import { FormField } from "components/ui/FormField";
import { Loader } from "components/ui/Loader/Loader";
import { SIGN_IN_ROUTE } from "constants/routesValues";
import { SITE_NAME } from "constants/siteName";
import { VALIDATION_SCHEMA } from "constants/validationSchema";
import { useSiteName } from "context/SiteName.context";
import { createRouteAction } from "utils/history.utils";
import { getRecaptchaToken } from "utils/recaptcha";
import style from "./ForgotPassword.module.scss";

type ForgotPasswordFormValues = {
  email: string;
};

const isValidationErrorsResponse = (value: unknown): value is Record<string, string[]> => {
  if (typeof value !== "object" || value === null) return false;
  return Object.values(value).every((v) => Array.isArray(v) && v.every((item) => typeof item === "string"));
};

export const ForgotPassword = () => {
  const { siteName } = useSiteName();
  const isChineseBrothers = siteName === SITE_NAME.CHINESE_BROTHERS;
  const [isForgotPasswordFetching, setIsForgotPasswordFetching] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isSuccessfulSending, setIsSuccessfulSending] = useState<string>("");

  const formMethods = useForm<ForgotPasswordFormValues>({
    defaultValues: {
      email: "",
    },
    mode: "onSubmit",
  });

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setErrorMessage("");
    setIsSuccessfulSending("");
    formMethods.clearErrors("email");
    setIsForgotPasswordFetching(true);

    let recaptcha: string;
    try {
      recaptcha = await getRecaptchaToken("FORGOT_PASSWORD");
    } catch {
      setIsForgotPasswordFetching(false);
      setErrorMessage("Error with reCAPTCHA verification.");
      return;
    }

    await authApi
      .forgotPassword({
        email: data.email,
        recaptcha,
      })
      .then((response) => {
        setIsForgotPasswordFetching(false);
        if (response.status === 200) {
          setIsSuccessfulSending(response.data?.message ?? "");
        }
      })
      .catch((error) => {
        setIsForgotPasswordFetching(false);
        const status = error.response?.status;
        const errors = error.response?.data?.errors;

        if (errors?.recaptcha) {
          setErrorMessage(errors.recaptcha[0] ?? "reCAPTCHA verification failed.");
          return;
        }

        if (status === 422 && isValidationErrorsResponse(errors)) {
          const emailError = errors.email?.[0];
          if (emailError) {
            formMethods.setError("email", { message: emailError });
          }

          return;
        }

        if (status === 500) {
          setErrorMessage("Server error. Please try again later.");
          return;
        }

        setErrorMessage(error.response?.data?.message || "Something went wrong.");
      });
  };

  return (
    <div
      className={cn(style.forgotPasswordWrapper, {
        [style.forgotPasswordWrapperChineseBrothers]: isChineseBrothers,
      })}
    >
      <h3 className={style.forgotPasswordTitle}>Forgot password?</h3>
      <FormProvider {...formMethods}>
        <form id="forgotPasswordForm" action="POST" onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Controller
            name="email"
            control={formMethods.control}
            rules={VALIDATION_SCHEMA.email}
            render={({ field, fieldState }) => (
              <FormField
                disabled={!!isSuccessfulSending}
                placeholder="johndoe@gmail.com"
                name={field.name}
                labelTitle="Email"
                type="email"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error}
              />
            )}
          />
          {isSuccessfulSending && <p className={style.successMessage}>{isSuccessfulSending}</p>}
          <Button
            disabled={isForgotPasswordFetching || !!isSuccessfulSending}
            type="submit"
            text={isForgotPasswordFetching ? "Please wait" : "Send reset link"}
            className={cn(style.formButton, { [style.formButtonChineseBrothers]: isChineseBrothers })}
          >
            {isForgotPasswordFetching && <Loader className={style.loader} />}
          </Button>
        </form>
        {errorMessage && <p className={style.errorMessage}>{errorMessage}</p>}
        <p className={style.formDescriptionLink}>
          Back to
          <span onClick={() => createRouteAction(SIGN_IN_ROUTE)}>Sign in</span>
        </p>
      </FormProvider>
    </div>
  );
};
